/**
 * @module web/persistence
 * @private
 */

import isBrowser from "../../utils/isBrowser";
import { layoutTokenStore } from "../store";
import reports from "../manager/reports";
const localForage = isBrowser
  ? require("starz-localforage")
  : {
      createInstance: () => ({
        iterate: () => {},
        getItem: () => {},
        setItem: () => {}
      })
    };

const databases = {
  layouts: null,
  titles: null,
  auth: null,
  search: null,
  genres: null,
  devices: null,
  preferences: null,
  channelevents: null,
  categoryEvents: null,
  startEpgEvents: null,
  epgConfigs: null,
  mops: null,
  subscriptions: true,
  deepAddOns: null,
  paymentMethodDictionary: null,
  allSubscriptionDeepQuery: null
};

let minimumCacheCleanupTime;
let sdkInstance = null;
let isReporting = false;
/**
 * Local cache manager that will deal with the storage and manager of data received by backend or the user. This
 * management involves storing, retrieving, removing and expiring data. For every stored data, the cache will attach an
 * expiration date and will periodically check for expired assets in the cache to remove them..gt
 */
export const cache = {
  /**
   * Sets the SDK instance for reporting purposes. This should be called after the SDK is created.
   * @param {Object} sdk - The SDK instance
   */
  setSDKInstance(sdk) {
    sdkInstance = sdk;
  },

  /**
   * Initializes the local databases the app will use and stores them in the databases object, which will be later used
   * in the internal methods of the cache module to manage each database as needed. Also initializes the periodic cache
   * cleanup that will remove expired items from the databases.
   * @returns {cache}
   */
  init(minCacheCleanupTime) {
    // TODO add a way to wipe out cached data following deprecated data models, to avoid crashes in the app
    minimumCacheCleanupTime = minCacheCleanupTime;
    databases.layouts = localForage.createInstance({
      name: "layouts",
      storeName: "layouts"
    });
    databases.titles = localForage.createInstance({
      name: "titles",
      storeName: "titles"
    });
    databases.auth = localForage.createInstance({
      name: "auth",
      storeName: "auth"
    });
    databases.search = localForage.createInstance({
      name: "search",
      storeName: "search"
    });
    databases.feeds = localForage.createInstance({
      name: "feeds",
      storeName: "feeds"
    });
    databases.genres = localForage.createInstance({
      name: "genres",
      storeName: "genres"
    });
    databases.devices = localForage.createInstance({
      name: "devices",
      storeName: "devices"
    });
    databases.preferences = localForage.createInstance({
      name: "preferences",
      storeName: "preferences"
    });
    databases.channelevents = localForage.createInstance({
      name: "channelevents",
      storeName: "channelevents"
    });
    databases.categoryEvents = localForage.createInstance({
      name: "categoryEvents",
      storeName: "categoryEvents"
    });
    databases.startEpgEvents = localForage.createInstance({
      name: "startEpgEvents",
      storeName: "startEpgEvents"
    });
    databases.epgConfigs = localForage.createInstance({
      name: "epgConfigs",
      storeName: "epgConfigs"
    });
    databases.mops = localForage.createInstance({
      name: "mops",
      storeName: "mops"
    });
    databases.subscriptions = localForage.createInstance({
      name: "subscriptions",
      storeName: "subscriptions"
    });
    databases.deepAddOns = localForage.createInstance({
      name: "deepAddOns",
      storeName: "deepAddOns"
    });
    databases.paymentMethodDictionary = localForage.createInstance({
      name: "paymentMethodDictionary",
      storeName: "paymentMethodDictionary"
    });
    databases.allSubscriptionDeepQuery = localForage.createInstance({
      name: "allSubscriptionDeepQuery",
      storeName: "allSubscriptionDeepQuery"
    });
    initCleaner();
    return this;
  },
  /**
   * Stores an item in a database with the specified key and value. The destination database will be specified inside
   * the config object's database parameter.
   * @param {string} key    A string that will be used to uniquely identify the stored value inthe database
   * @param {object} value  The value to be stored, it can be any serializable object.
   * @param {object} config A config object to specify extra parameters to the method:
   *    - database: The name of the database to store the value in. Must be one of layouts, titles, auth, search
   *    - expiresIn: A timestamp in milliseconds to consider the cached value relevant. After this timestamp is elapsed
   *    the item will be marked as removable and eventually removed from the database.
   *    - expirationDate: A fixed date in milliseconds on which the item will be considered removable. If both
   *    expirationDate and expiresIn are provided, expiresIn will be used.
   * @returns {Promise} A promise that will be resolved when the operation is completed.
   */
  set: async function set(key, value, config) {
    // Creating a copy of the object (either object or array) to avoid changing the original object.
    const valueToCache = JSON.parse(JSON.stringify(value));
    if (config.expiresIn) {
      valueToCache.cacheExpirationDate = new Date().getTime() + config.expiresIn;
    } else if (config.expirationDate) {
      valueToCache.cacheExpirationDate = config.expirationDate;
    }

    // TODO: Commenting out reporting for now, as it was causing performance issues. Revisit if needed.
    // if (config.database === "auth" && sdkInstance && !isReporting) {
    //   isReporting = true;
    //   try {
    //     await reports.sendReport.call(
    //       sdkInstance,
    //       { level: "info", event: "IndexDB", env: "production" },
    //       {
    //         eventId: "IndexDBLog",
    //         parameter: {
    //           errorCode: "571201",
    //           message: ` Auth cache Write for key: ${key} in database ${config.database} and value: ${JSON.stringify(
    //             valueToCache
    //           )}`,
    //           user: await sdkInstance.userManager.get()
    //         },
    //         report: "571201",
    //         status: "info"
    //       }
    //     );
    //   } finally {
    //     isReporting = false;
    //   }
    // }

    return databases[config.database].setItem(key, valueToCache);
  },
  /**
   * Returns a value identified by the key parameter and from the database specified in the config objects database
   * property.
   * @param key
   * @param config
   * @returns {*}
   */
  get: async function get(key, config) {
    const item = await databases[config.database].getItem(key);
    if (item && item.cacheExpirationDate > new Date().getTime()) {
      delete item.cacheExpirationDate;
      return item;
    }
    return undefined;
  },
  /**
   * Removes the item identified by the key parameter from the database specified in the config.database property.
   * @param {string} key    The key of the element that needs to be removed from the database.
   * @param {object} config A config object to specify extra parameters to the method:
   *    - database: The name of the database to store the value in. Must be one of layouts, titles, auth, search
   * @returns {Promise} A promise that will be resolved when the operation is completed.
   */
  remove: async function remove(key, config) {
    // TODO: Commenting out reporting for now, as it was causing performance issues. Revisit if needed.
    // if (config.database === "auth" && sdkInstance && !isReporting) {
    //   isReporting = true;
    //   try {
    //     await reports.sendReport.call(
    //       sdkInstance,
    //       { level: "info", event: "IndexDB", env: "production" },
    //       {
    //         eventId: "IndexDBLog",
    //         parameter: {
    //           errorCode: "571201",
    //           message: ` Auth cache Remove for key: ${key} in database ${config.database} `,
    //           user: await sdkInstance.userManager.get()
    //         },
    //         report: "571201",
    //         status: "info"
    //       }
    //     );
    //   } finally {
    //     isReporting = false;
    //   }
    // }
    return databases[config.database].removeItem(key);
  },
  /**
   * Clears all the keys from the database specified in the config object's database parameter.
   * @param {object} config A config object to specify extra parameters to the method:
   *    - database: The name of the database to store the value in. Must be one of layouts, titles, auth, search
   * @returns {Promise} A promise that will be resolved when the operation is completed.
   */
  clear(config) {
    return databases[config.database] && databases[config.database].clear();
  },
  /**
   * Clears all the keys from all the databases.
   */
  clearAll() {
    Object.keys(databases).forEach(databaseName => databases[databaseName].clear());
  },
  /**
   * Clears all the keys matching a provided string inside the specified database name.
   * @param {String} databaseName - Database name.
   * @param {String} string - String to filter database keys.
   */
  async clearAllFilteredKeysFromDatabase(databaseName, string) {
    const database = databases[databaseName];
    const keys = await database.keys();
    return keys.forEach(key => key.includes(string) && database.removeItem(key));
  }
};

/**
 * A utility function that will periodically remove all the stored data that is considered expired. The time on which the
 * removal will happen will not be necessarily the same specified when the value was stored. The time value will mark
 * the content as removable and the cleaner will eventually remove all the elements satisfying that condition. This
 * function performs an initial data removal of those elements with an expiration date that is already in the past and
 * after that waits until the next element is expired. When this happens, another cleanup will start repeating all the
 * steps.
 */
export function initCleaner() {
  // TODO If web worker exists, init one and do the work in there
  // TODO else, just do the work with a setTimeout to put this in the tail of the queue
  setTimeout(function() {
    const now = new Date().getTime();
    const nextExpirationDate = removeExpiredValues(now);
    let timeToNextExpirationDate = nextExpirationDate - now;
    setTimeout(initCleaner, timeToNextExpirationDate);
  }, 0);
}

/**
 * Loops all the existing databases and removes all the values whose expirationDate is before the limitDate parameter.
 * At the same time, it will calculate what is the expirationDate of the element that is closest in time to expire and
 * returns the value. If this value is less than MINIMUM_CACHE_CLEANUP_TIME, MINIMUM_CACHE_CLEANUP_TIME will be returned
 * instead, to avoid to try the cleanup too often.
 * @param {number} limitDate  Date that will be used as the limit to consider elements expired. Those elements before
 * this date will be removed from the databases.
 * @returns {number}  The date when the closest element to expire will expire.
 */
function removeExpiredValues(limitDate) {
  let nextExpirationDate = null;
  const defaultCacheCleanupDate = new Date().getTime() + minimumCacheCleanupTime;
  const limit = limitDate || new Date().getTime();
  Object.values(databases).forEach(database => {
    database.iterate((value, key) => {
      if (value.cacheExpirationDate < limit) {
        database.removeItem(key);
        key.indexOf("-modules") > -1 && layoutTokenStore.clear(); // TODO Create an event system to make this agnostic (onItemAdded, onItemRemoved, onDatabaseCleared, onItemTimedOut)
      } else if (!nextExpirationDate || value.cacheExpirationDate < nextExpirationDate) {
        nextExpirationDate = value.cacheExpirationDate;
      }
    });
  });
  return nextExpirationDate > defaultCacheCleanupDate ? nextExpirationDate : defaultCacheCleanupDate;
}
